import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as amplitude from '@amplitude/analytics-browser';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useIdleTimer } from 'react-idle-timer';
import * as userActions from '../actions/userActions';
import signOutUrl from '../lib/signOutUrl';
import Footer from './Footer';
import LogoffWarningModal from '../components/LogoffWarningModal';
import HeaderContainer from '../containers/HeaderFormContainer';
import TokenTimerContainer from '../containers/TokenTimerContainer';
import configureDatadog, { setDatadogUser } from '../lib/configureDatadog';
import { findContactByType } from '../containers/contactSearchAndFormat';
import { trackEvent } from '../lib/amplitude';
import AmplitudeFlags from '../lib/amplitudeFlags';
import Config from '../Config';

configureDatadog();

const config = new Config();
const timeout = config.limitTimeMilliSeconds;
const promptBeforeIdle = config.warningTimeMilliSeconds;

if (config.amplitudeEnabled && config.amplitudeKey) {
  amplitude.init(config.amplitudeKey);
}

const paddingTop = {
  paddingTop: '0px',
};

const App = (props) => {
  const isAgreementsPage = window.location.pathname === '/agreements';
  const hideHeaderFor = ['/intro', '/logoff'];
  const hideFooterFor = ['/logoff'];
  const hideHeader = hideHeaderFor.includes(window.location.pathname);
  const hideFooter = hideFooterFor.includes(window.location.pathname);

  const [remaining, setRemaining] = useState(timeout);
  const [open, setOpen] = useState(false);

  const primaryContact = findContactByType('primary', props.contact);
  const email = primaryContact ? primaryContact.email : null;
  const fdcnId = props.registration ? props.registration.fdcnId : null;
  // Prefer the applicant's own name; fall back to the logged-in user, which auth
  // bootstrap loads before any contact fetch happens on a returning session.
  const nameSource = (primaryContact && primaryContact.firstName) ? primaryContact : props.user;
  const name = nameSource
    ? [nameSource.firstName, nameSource.lastName].filter(Boolean).join(' ')
    : null;

  // Identify the session in Datadog RUM. Runs again whenever a field lands, since
  // the contact, user and registration all resolve later than the application id.
  useEffect(() => {
    setDatadogUser({
      applicationId: props.applicationId, email, name, fdcnId,
    });
  }, [props.applicationId, email, name, fdcnId]);

  // Resolve Amplitude feature flags as soon as the applicant is identified. The
  // client caches variants, so later reads are synchronous by the time a page
  // deep in the wizard needs one.
  useEffect(() => {
    AmplitudeFlags.initialize(fdcnId);
  }, [fdcnId]);

  const handleTrackEvent = (eventType) => {
    const isTseu = props.application && props.application.isTSEU;
    const eventProperties = {
      exit_method: eventType,
      promo_code: props.registration ? props.registration.promoCode : null,
      application_id: props.applicationId,
      tradestation_entity: isTseu ? 'tseu' : 'tss',
    };
    trackEvent('aop_exit', eventProperties, fdcnId);
  };

  const onIdle = () => {
    handleTrackEvent('time_out');
    setOpen(false);
    if (props.authToken) {
      props.actions.userLogout(props.authToken)
        .then(() => {
          setTimeout(() => {
            window.location = signOutUrl('/logout.html');
          }, 500); // 0.5 second delay so that handleTrackEvent runs
        });
    }
  };

  const onActive = () => {
    setOpen(false);
  };

  const onPrompt = () => {
    if (props.authToken) {
      setOpen(true);
    }
  };

  const { getRemainingTime, activate } = useIdleTimer({
    onIdle,
    onActive,
    onPrompt,
    timeout,
    promptBeforeIdle,
    throttle: 500,
    events: ['keydown', 'mousedown', 'touchstart', 'MSPointerDown'],
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(Math.ceil(getRemainingTime() / 1000));
    }, 500);

    return () => {
      clearInterval(interval);
    };
  });

  const handleStillHere = () => {
    activate();
  };

  return (
    <div>
      {!hideHeader &&
        <HeaderContainer
          user={props.user}
          authToken={props.authToken}
          userId={props.userId}
          location={window.location.pathname}
        />
      }
      <div className="notranslate" style={paddingTop}>
        {props.children}
      </div>
      {!isAgreementsPage && !hideFooter &&
        <Footer />
      }
      {config.auth0Enabled &&
        <TokenTimerContainer authToken={props.authToken} />
      }
      <LogoffWarningModal
        showModal={open}
        toggleWarning={handleStillHere}
        timeLeft={remaining}
      />
    </div>
  );
};

App.propTypes = {
  user: PropTypes.shape(),
  authToken: PropTypes.string,
  userId: PropTypes.number,
  applicationId: PropTypes.string,
  application: PropTypes.shape(),
  contact: PropTypes.arrayOf(PropTypes.shape()),
  children: PropTypes.element,
  actions: PropTypes.shape({
    userLogout: PropTypes.func.isRequired,
  }).isRequired,
  registration: PropTypes.shape(),
};

function mapStateToProps(state) {
  return state;
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Object.assign({},
    userActions),
    dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(App);
